import type { FC } from 'react';
import React, { useRef } from 'react';
import classNames from 'clsx';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import { Video } from '@wix/video';

import type { VideoUploadProps } from '../VideoUpload.types';
import semanticClassNames from '../VideoUpload.semanticClassNames';
import { useVideoPlayer } from './hooks';
import { PlayButtonIcon } from './assets/PlayButtonIcon';
import { PlaybackControls } from './components/PlaybackControls/PlaybackControls';
import { Spinner } from './components/Spinner/Spinner';
import styles from './VideoUpload.module.scss';

const VideoUpload: FC<VideoUploadProps> = ({
  id,
  className,
  customClassNames = [],
  video,
  autoplay = false,
  loop = false,
  mute = true,
  enableControls = true,
  enableTitle = false,
  startTime = 0,
  endTime,
  playbackRate = 1,
  a11y,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    timings,
    showTitle,
    showControls,
    playback,
    playbackSettings,
    audio,
    pip,
    fullscreen,
    buffer,
    aspectRatio,
    showPlayButton,
    showSpinner,
    handlers,
  } = useVideoPlayer({
    videoRef,
    containerRef,
    video,
    autoplay,
    playbackRate,
    enableTitle,
    enableControls,
    loop,
    startTime,
    endTime,
  });
  const {
    onVideoContainerClick,
    onVideoContainerDoubleClick,
    onVideoPlayerKeyDown,
    onVideoPlayerMouseEnter,
    onVideoPlayerMouseMove,
    onVideoPlayerMouseLeave,
    onVideoPlayerFocus,
    onPlayButtonOverlayClick,
  } = handlers;

  return (
    <div
      id={id}
      className={classNames(
        styles.root,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
    >
      {video && video.poster ? (
        <div
          className={classNames(styles.videoPlayerWrapper, {
            [styles.lockedAspectRatio]: aspectRatio,
          })}
        >
          <div
            ref={containerRef}
            className={classNames(styles.videoPlayerContainer, {
              [styles.lockedAspectRatio]: aspectRatio,
              [styles.fullscreen]: fullscreen.isFullscreen,
            })}
            style={
              aspectRatio
                ? ({ '--aspect-ratio': aspectRatio } as React.CSSProperties)
                : undefined
            }
            tabIndex={0}
            onKeyDown={onVideoPlayerKeyDown}
            onMouseEnter={onVideoPlayerMouseEnter}
            onMouseMove={onVideoPlayerMouseMove}
            onMouseLeave={onVideoPlayerMouseLeave}
            onFocus={onVideoPlayerFocus}
          >
            <div
              className={styles.videoContainer}
              onClick={onVideoContainerClick}
              onDoubleClick={onVideoContainerDoubleClick}
            >
              <Video
                id={`wix-video-${id}`}
                videoRef={videoRef}
                videoMedia={{ ...video, name: a11y?.ariaLabel || video.name }}
                muted={autoplay || mute}
                loop={loop}
                autoplay={autoplay}
                qualityPolicy="adaptive"
                playbackRate={playbackRate}
              />
              {showSpinner && (
                <div className={styles.spinnerContainer}>
                  <Spinner aria-hidden="true" />
                </div>
              )}
            </div>

            {enableTitle && !!video.name && (
              <div
                className={classNames(styles.titleContainer, {
                  [styles.visible]: showTitle,
                })}
              >
                <h2
                  className={classNames(
                    styles.title,
                    formatClassNames(semanticClassNames.title),
                  )}
                >
                  {video.name}
                </h2>
              </div>
            )}

            {showPlayButton && (
              <div
                className={styles.playButtonContainer}
                onClick={onPlayButtonOverlayClick}
              >
                <button
                  className={styles.playButton}
                  type="button"
                  tabIndex={0}
                  aria-label="Play video"
                >
                  <PlayButtonIcon aria-hidden="true" />
                </button>
              </div>
            )}

            {enableControls && !showPlayButton && (
              <div
                className={classNames(styles.playbackControlsContainer, {
                  [styles.visible]: showControls,
                })}
              >
                <PlaybackControls
                  startTime={timings.startTime}
                  endTime={timings.endTime}
                  currentTime={playback.currentTime}
                  volume={audio.volume}
                  playbackRate={playbackSettings.playbackRate}
                  isPlaying={playback.isPlaying}
                  hasAudio={audio.hasAudio}
                  isMuted={audio.isMuted}
                  isFullscreen={fullscreen.isFullscreen}
                  isPictureInPicture={pip.isPictureInPicture}
                  fullscreenSupported={fullscreen.isFullscreenEnabled}
                  pictureInPictureSupported={pip.isPictureInPictureEnabled}
                  loadedPercentage={buffer.loadedPercentage}
                  onPlay={playback.play}
                  onPause={playback.pause}
                  onSeek={playback.seek}
                  onMuteToggle={audio.toggleMuted}
                  onVolumeChange={audio.setVolume}
                  onPlaybackRateChange={playbackSettings.setPlaybackRate}
                  onEnterFullscreen={fullscreen.enterFullscreen}
                  onExitFullscreen={fullscreen.exitFullscreen}
                  onPictureInPictureToggle={pip.togglePictureInPicture}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <p>No video uploaded</p>
      )}
    </div>
  );
};

export default VideoUpload;
